// ============================================================================
// apercu — estimation de la coupe d'une publication sur un ecran de telephone
// ============================================================================
// PublicationCard borne le corps a 3 lignes (line-clamp-3) et ne coupe jamais
// le titre. Le vrai debordement se mesure au rendu (hauteur DOM) — invisible a
// l'editeur, qui doit avertir AVANT que l'on publie, et a jsdom, qui ne calcule
// aucune hauteur. On ESTIME donc en lignes, a la largeur du cadre de l'apercu :
// texte du corps 14 px Inter (~7 px par caractere) dans une carte p-5 d'un
// ecran de 360 px = ~296 px utiles, soit ~40 caracteres par ligne. L'estimation
// est volontairement prudente (mots entiers renvoyes a la ligne) : elle sert un
// avertissement DOUX, jamais un refus.

/** Caracteres par ligne a la largeur du telephone de l'apercu. */
export const PHONE_CHARS_PER_LINE = 40;
/** Lignes du corps visibles avant « Lire la suite » (line-clamp-3 de PublicationCard). */
export const BODY_VISIBLE_LINES = 3;
/** Au-dela, le titre occupe trop de place en tete de carte sur le telephone. */
export const TITLE_COMFORT_LINES = 2;
/** Bornes des champs — alignees sur la validation serveur du composeur (maxLength). */
export const TITLE_MAX = 300;
export const BODY_MAX = 10000;

/** Nombre de lignes qu'occupe un paragraphe, mots entiers renvoyes a la ligne. */
function wrapParagraph(paragraph: string, perLine: number): string[] {
  const words = paragraph.split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    // Un mot plus long qu'une ligne se casse (URL, mot compose).
    let rest = word;
    while (rest.length > perLine) {
      if (current) {
        lines.push(current);
        current = '';
      }
      lines.push(rest.slice(0, perLine));
      rest = rest.slice(perLine);
    }
    const candidate = current ? `${current} ${rest}` : rest;
    if (candidate.length <= perLine) {
      current = candidate;
    } else {
      lines.push(current);
      current = rest;
    }
  }
  lines.push(current);
  return lines;
}

/** Lignes affichees (whitespace-pre-line : les retours a la ligne sont gardes). */
export function wrapLines(text: string, perLine = PHONE_CHARS_PER_LINE): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  return trimmed.split(/\r?\n/).flatMap((p) => wrapParagraph(p, perLine));
}

export interface CutEstimate {
  titleLines: number;
  /** Titre de plus de TITLE_COMFORT_LINES lignes sur le telephone. */
  titleLong: boolean;
  bodyLines: number;
  /** Corps coupe a BODY_VISIBLE_LINES lignes, la suite derriere « Lire la suite ». */
  bodyCut: boolean;
  /** Ce que l'employe lit avant de deplier (les lignes visibles, jointes). */
  visibleExcerpt: string;
}

export function estimateCut(title: string, body: string): CutEstimate {
  const titleLines = wrapLines(title).length;
  const lines = wrapLines(body);
  const bodyCut = lines.length > BODY_VISIBLE_LINES;
  return {
    titleLines,
    titleLong: titleLines > TITLE_COMFORT_LINES,
    bodyLines: lines.length,
    bodyCut,
    visibleExcerpt: lines.slice(0, BODY_VISIBLE_LINES).join(' ').trim(),
  };
}
